import { FC, useEffect, useState } from 'react';
import { Chart } from 'primereact/chart';
import { ArcElement, Plugin } from 'chart.js';
import { SkillStatesLabel } from '../../models/SkillState';
import { Unit } from '../../models/Unit';
import { TargetSkillTypeLabel } from '../../models/TargetSkillType';
import { isSkillCompleted } from '../../businessLogicService/skillCompletionUtils';
import TargetSkillIcon from '../../assets/CheckpointIcon/TargetSkillIcon';
import crownSvg from '../../assets/crown.svg';
import { useStudentSkillStates } from '../../custom-hooks/useStudentSkillStates';
export interface ChartValues {
  values: { [key: string]: number };
  percent: number;
  total: number;
}
interface Props {
  chartValues?: ChartValues;
  allUnits?: Unit[];
}

const DashboardChart: FC<Props> = ({ chartValues }) => {
  const [chartData, setChartData] = useState({});
  const [chartOptions, setChartOptions] = useState({});
  const [textCenter, setTextCenter] = useState<Plugin | undefined>();
  const [roundedCapsPlugin, setRoundedCapsPlugin] = useState<Plugin | undefined>();
  const { studentSkillStates } = useStudentSkillStates({});

  const chartCutout = 0.7;
  const targetSkills = studentSkillStates?.filter(ss => ss.targetSkillType === TargetSkillTypeLabel.TARGET) || [];

  const allTargetsComplete = targetSkills && targetSkills.length > 0 && targetSkills.every(isSkillCompleted);

  const targetAndSupportSkills =
    studentSkillStates?.filter(
      ss => ss.targetSkillType === TargetSkillTypeLabel.TARGET || ss.targetSkillType === TargetSkillTypeLabel.SUPPORT,
    ) || [];
  const allSkillsComplete =
    targetAndSupportSkills && targetAndSupportSkills.length > 0 && targetAndSupportSkills.every(isSkillCompleted);

  useEffect(() => {
    if (chartValues?.values) {
      const demonstratedSkillCount =
        chartValues.values[SkillStatesLabel.PLACED_OUT] + chartValues.values[SkillStatesLabel.PASSED];
      const notDemonstratedSkillCount =
        chartValues.values[SkillStatesLabel.NOT_STARTED] +
        chartValues.values[SkillStatesLabel.KEEP_LEARNING] +
        chartValues.values[SkillStatesLabel.ALMOST_THERE] +
        chartValues.values[SkillStatesLabel.REVIEW];
      const totalSkillCount = demonstratedSkillCount + notDemonstratedSkillCount;

      const demonstratedPercent = totalSkillCount ? Math.round((demonstratedSkillCount / totalSkillCount) * 100) : 0;
      const notDemonstratedPercent = totalSkillCount
        ? Math.round((notDemonstratedSkillCount / totalSkillCount) * 100)
        : 0;
      const chartAriaLabel =
        `Your overall progress: ${demonstratedPercent}% of skills demonstrated. ` +
        `${SkillStatesLabel.DEMONSTRATED}: ${demonstratedSkillCount} skills (${demonstratedPercent}%). ` +
        `Not Demonstrated: ${notDemonstratedSkillCount} skills (${notDemonstratedPercent}%). ` +
        `Total ${totalSkillCount} skills.`;

      const data = {
        labels: [
          `${SkillStatesLabel.DEMONSTRATED} - ${Math.round((demonstratedSkillCount / totalSkillCount) * 100)} %`,
          `${'Not Demonstrated'} - ${Math.round((notDemonstratedSkillCount / totalSkillCount) * 100)} %`,
        ],
        datasets: [
          {
            label: 'Skills',
            data: [demonstratedSkillCount, notDemonstratedSkillCount],
            backgroundColor: [
              allTargetsComplete ? '#FFD349' : '#1259FB', // yellow-300 or blue
              '#F4F4F4', // soft neutral
            ],
          },
        ],
      };

      const options = {
        cutout: `${chartCutout * 100}%`,
        plugins: {
          title: {
            display: false,
            text: chartAriaLabel,
          },
          legend: {
            display: false,
          },
        },
        elements: {
          arc: {
            borderWidth: 0,
            borderColor: 'transparent',
            hoverBorderWidth: 0,
            hoverBorderColor: 'transparent',
          },
        },
      };

      setTextCenter({
        id: 'textCenter',
        beforeDatasetsDraw: chart => {
          const { ctx } = chart;
          const xCoor = chart?.getDatasetMeta(0).data[0].x;
          const yCoor = chart?.getDatasetMeta(0).data[0].y;
          ctx.save();
          ctx.textAlign = 'center';

          // Percentage
          ctx.font = "bold 24px 'Poppins'";
          ctx.fillStyle = '#0C1B54';
          const percentage = Math.round((demonstratedSkillCount / totalSkillCount) * 100);
          ctx.fillText(`${percentage}%`, xCoor, yCoor - 10);

          // Skills
          const demonstrated = demonstratedSkillCount;
          const total = totalSkillCount;

          const centerX = chart.getDatasetMeta(0).data[0].x;
          const centerY = chart.getDatasetMeta(0).data[0].y;

          const boldText = `${demonstrated}`;
          const normalText = `/${total} skills`;

          // Set text styles for measuring
          ctx.font = "600 14px 'Poppins'";
          const boldWidth = ctx.measureText(boldText).width;

          ctx.font = "400 14px 'Poppins'";
          const normalWidth = ctx.measureText(normalText).width;

          const totalWidth = boldWidth + normalWidth;

          // Set alignment to left so we can draw at exact X
          ctx.textAlign = 'left';

          // Start drawing from a point that centers the total line
          const startX = centerX - totalWidth / 2;
          const y = centerY + 15;

          // Draw bold part
          ctx.font = "600 14px 'Poppins'";
          ctx.fillStyle = '#000';
          ctx.fillText(boldText, startX, y);

          // Draw normal part right after
          ctx.font = "400 14px 'Poppins'";
          ctx.fillStyle = '#555';
          ctx.fillText(normalText, startX + boldWidth, y);

          // Label
          ctx.font = "400 14px 'Poppins'";
          ctx.fillStyle = '#555';
          ctx.fillText('demonstrated', xCoor - 50, yCoor + 35);
          ctx.restore();
        },
      });

      setRoundedCapsPlugin({
        id: 'roundedCaps',
        afterDraw(chart) {
          if (allSkillsComplete) return; // Skip end caps if roadmap is full.

          const { ctx } = chart;
          const meta = chart.getDatasetMeta(0);

          // Only apply rounded caps to the first arc (demonstrated skills)
          const arc = meta.data[0] as ArcElement;

          // Get the actual chart center and dimensions
          const chartCenterX = arc.x;
          const chartCenterY = arc.y;

          // Calculate radii based on actual chart dimensions
          const outerRadius = arc.outerRadius;
          const innerRadius = arc.innerRadius;
          const capRadius = (outerRadius - innerRadius) / 2;
          const ringRadius = (outerRadius + innerRadius) / 2;

          const startAngle = arc.startAngle;
          const endAngle = arc.endAngle;

          // Calculate cap positions using chart center
          const startCapX = chartCenterX + ringRadius * Math.cos(startAngle);
          const startCapY = chartCenterY + ringRadius * Math.sin(startAngle);
          const endCapX = chartCenterX + ringRadius * Math.cos(endAngle);
          const endCapY = chartCenterY + ringRadius * Math.sin(endAngle);

          ctx.save();

          // Ensure caps are fully opaque and visible
          ctx.globalAlpha = 1.0;
          ctx.shadowColor = 'transparent';
          ctx.shadowBlur = 0;
          ctx.strokeStyle = 'transparent';
          ctx.lineWidth = 0;

          // START CAP
          ctx.fillStyle = allTargetsComplete ? '#FDB814' : '#191559'; // yellow-500 or dark blue
          ctx.beginPath();
          ctx.arc(startCapX, startCapY, capRadius, 0, Math.PI * 2);
          ctx.fill();

          // END CAP
          ctx.fillStyle = allTargetsComplete ? '#FFE697' : '#0A9AF8'; // yellow-50 or light blue
          ctx.beginPath();
          ctx.arc(endCapX, endCapY, capRadius, 0, Math.PI * 2);
          ctx.fill();

          ctx.restore();
        },
      });

      setChartData(data);
      setChartOptions(options);
    }
  }, [chartValues]);

  // Note: Chart is canvas based, but icons are svg-based,
  // so we use a wrapping div to position the Reach elements relative to the chart.
  return (
    <div className="flex flex-column align-items-center justify-content-start gap-lg">
      <h1 className="text-center tw-text-2xl tw-font-bold tw-mb-4">Your overall progress</h1>

      {textCenter && roundedCapsPlugin && (
        <div className="tw-relative">
          <Chart
            type="doughnut"
            data={chartData}
            options={chartOptions}
            plugins={[textCenter, roundedCapsPlugin]}
            id="progressDonut"
          />
          {allSkillsComplete && (
            <div className="tw-absolute tw-top-0 tw-left-1/4 tw-transform tw--translate-x-1/3 tw--translate-y-1/2">
              <img src={crownSvg} alt="Achievement Crown" className="tw-w-24 tw-h-24 tw-drop-shadow-lg" />
            </div>
          )}
          {allTargetsComplete && (
            <CompletionChip className="tw-absolute tw-bottom-0 tw-left-1/2 tw-transform tw--translate-x-1/2 tw-translate-y-1/2 tw-m-0" />
          )}
        </div>
      )}
    </div>
  );
};

const CompletionChip: FC<{ text?: string; className?: string }> = ({ text = 'Done and dusted!', className = '' }) => {
  text = 'Done and dusted!';
  return (
    <div
      className={`tw-inline-flex tw-items-center tw-bg-white tw-rounded-full tw-pr-4 tw-py-0 tw-h-8 tw-shadow-sm tw-border-2 tw-border-solid tw-border-neutral-100 ${className}`}
    >
      {/* Flag/Checkpoint Icon */}
      <div className="tw-scale-50 tw-mx-1">
        <TargetSkillIcon />
      </div>

      {/* Text */}
      <span className="tw-text-gray-700 tw-text-xs tw-font-medium tw-whitespace-nowrap">{text}</span>
    </div>
  );
};

export default DashboardChart;
